
"use client"

import * as React from "react"
import { useMemo } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { Timestamp } from "firebase/firestore"

import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Pencil } from "lucide-react"
import type { Expense } from "@/types/expense"

const formSchema = z.object({
  description: z.string().min(2, {
    message: "Description must be at least 2 characters.",
  }),
  amount: z.coerce.number().positive({
    message: "Amount must be a positive number.",
  }),
  retailer: z.string().optional(),
  employer: z.enum(["NOSS", "UBER"]).optional(),
  month: z.coerce.number().min(1, "1-12").max(12, "1-12"),
  day: z.coerce.number().min(1, "1-31").max(31, "1-31"),
  year: z.coerce.number().min(1900, "1900-2100").max(2100, "1900-2100"),
}).refine((data) => {
  const date = new Date(data.year, data.month - 1, data.day);
  return date.getFullYear() === data.year && date.getMonth() === data.month - 1 && date.getDate() === data.day;
}, {
  message: "Invalid date",
  path: ["day"],
});

interface EditExpenseFormProps {
  expense: Expense;
  onUpdateExpense: (values: { amount: number, description: string, retailer?: string, employer?: string, date: Date }) => void;
  onDone: () => void;
  isWorkCard?: boolean;
}

export function EditExpenseForm({ expense, onUpdateExpense, onDone, isWorkCard }: EditExpenseFormProps) {
  const initialDate = useMemo(() => {
    if (expense.date instanceof Timestamp) return expense.date.toDate();
    if (typeof expense.date === 'string') {
      const d = new Date(expense.date);
      return isNaN(d.getTime()) ? new Date() : d;
    }
    if ((expense.date as any)?.seconds && typeof (expense.date as any).seconds === 'number') {
      return new Date((expense.date as any).seconds * 1000);
    }
    return new Date();
  }, [expense.date]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      description: expense.description,
      amount: expense.amount,
      retailer: expense.retailer || "",
      employer: expense.employer || (isWorkCard ? "NOSS" : undefined),
      month: initialDate.getMonth() + 1,
      day: initialDate.getDate(),
      year: initialDate.getFullYear(),
    },
  })

  function onSubmit(values: z.infer<typeof formSchema>) {
    const date = new Date(values.year, values.month - 1, values.day);
    onUpdateExpense({
      amount: values.amount,
      description: values.description,
      retailer: values.retailer,
      employer: values.employer,
      date: date,
    })
    onDone();
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Input placeholder="e.g., Coffee with client" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Amount ($)</FormLabel>
              <FormControl>
                <Input type="number" step="0.01" placeholder="e.g., 4.50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="space-y-2">
          <FormLabel>Date of Entry (MM/DD/YYYY)</FormLabel>
          <div className="grid grid-cols-3 gap-2">
            <FormField
              control={form.control}
              name="month"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input type="number" placeholder="MM" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="day"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input type="number" placeholder="DD" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="year"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input type="number" placeholder="YYYY" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {isWorkCard && (
          <>
            <FormField
              control={form.control}
              name="employer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employer</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select employer" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="NOSS">NOSS</SelectItem>
                      <SelectItem value="UBER">UBER</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="retailer"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Retailer (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Starbucks" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}
        <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onDone} className="w-full">
                Cancel
            </Button>
            <Button type="submit" className="w-full">
                <Pencil className="mr-2 h-4 w-4" /> Save Changes
            </Button>
        </div>
      </form>
    </Form>
  )
}
